import {
  GaugeIcon,
  InfoIcon,
  SmileyIcon,
  SmileyMehIcon,
  SmileySadIcon,
} from "@phosphor-icons/react";
import type React from "react";
import Sparkle from "@/assets/svg/Sparkle.svg?react";
import { Separator } from "@/components/ui/controls/separator";
import { TooltipProvider } from "@/components/ui/data-display/tooltip";
import RationaleTooltip from "@/components/ui/molecules/feed-article-tooltips/RationaleTooltip";
import { cn } from "@/lib/utils";
import { useFeedName } from "@/store/news-feeds/news-feeds.selectors";
import SentimentTooltip from "./sentiment-tooltip";
import { isAISentiment as calculateIsAISentiment } from "./utils";

interface ScoreDisplayProps {
  impactScore: number | null | undefined;
  sentimentPolarity: string | null | undefined;
  sentimentRationale: string | null | undefined;
  sentimentPolarityOriginal: string | null | undefined;
}

// Common styles
const styles = {
  textStyle: "text-gray-800",
  iconColor: "text-violet-800",
  header: "text-xs leading-7 font-semibold",
  infoIcon:
    "text-gray-600 hover:text-slate-800 focus:text-slate-800 active:text-violet-800",
};

// Section header component
const SectionHeader: React.FC<{
  title: string;
  tooltipTitle: React.ReactNode;
  tooltipContent: string;
  tooltipIcon: React.ReactNode;
  showSparkle?: boolean;
  titleAttr?: string;
}> = ({
  title,
  tooltipTitle,
  tooltipContent,
  tooltipIcon,
  showSparkle,
  titleAttr,
}) => (
  <div className="flex flex-row space-x-1 items-center">
    <div className={`${styles.textStyle} ${styles.header}`} title={titleAttr}>
      {title}
    </div>
    {showSparkle && <Sparkle />}
    <SentimentTooltip
      title={tooltipTitle}
      content={tooltipContent}
      icon={tooltipIcon}
    >
      <InfoIcon className={styles.infoIcon} />
    </SentimentTooltip>
  </div>
);

interface ArticleSentimentPolarityProps {
  sentimentPolarity: string | null | undefined;
  sentimentRationale: string | null | undefined;
}

const ArticleSentimentPolarity: React.FC<ArticleSentimentPolarityProps> = ({
  sentimentPolarity,
  sentimentRationale,
}) => {
  const feedName = useFeedName();

  const currentRationale = sentimentRationale ?? "";
  const currentPolarity = sentimentPolarity?.toLowerCase() ?? "";

  const getSentimentIcon = (polarity: string | undefined, size?: number) => {
    switch (polarity) {
      case "positive":
        return <SmileyIcon size={size ?? 24} />;
      case "negative":
        return <SmileySadIcon size={size ?? 24} />;
      default:
        return <SmileyMehIcon size={size ?? 24} />;
    }
  };

  return (
    <div
      className={cn(
        "p-1 -m-1 mr-1 flex flex-row space-x-1 items-center text-sm text-gray-700/90 font-medium",
        currentPolarity === "positive" && "text-green-700",
        currentPolarity === "negative" && "text-rose-700/90",
      )}
    >
      {currentPolarity ? (
        <RationaleTooltip
          rationale={currentRationale || null}
          title={`Why is this article ${currentPolarity} for ${feedName}?`}
          icon={
            <span
              className={cn(
                currentPolarity === "positive" && "text-green-800",
                currentPolarity === "negative" && "text-rose-700/90",
              )}
            >
              {getSentimentIcon(currentPolarity, 16)}
            </span>
          }
        >
          {getSentimentIcon(currentPolarity)}
          <span className="first-letter:uppercase font-semibold text-sm">
            {currentPolarity}
          </span>
        </RationaleTooltip>
      ) : (
        <>-</>
      )}
    </div>
  );
};

const ScoreDisplay: React.FC<ScoreDisplayProps> = ({
  impactScore,
  sentimentPolarity,
  sentimentRationale,
  sentimentPolarityOriginal,
}) => {
  const isAISentiment = calculateIsAISentiment(
    sentimentPolarity,
    sentimentPolarityOriginal,
  );
  const rationale = sentimentRationale ?? undefined;

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-2.5 pt-7 w-30">
        <div className="-mb-1">
          <SectionHeader
            title="LLM Impact"
            tooltipTitle="LLM Impact Score"
            tooltipContent="This is a proprietary scoring system that is based on the article's prominence (headline, feature, lede, or passing mention classification), readership, social engagement, domain authority, and sentiment."
            tooltipIcon={<GaugeIcon className="w-4 h-4 text-violet-800" />}
          />

          <div className="inline-flex space-x-1 text-violet-800 items-center">
            <SentimentTooltip
              icon={<GaugeIcon className="w-4 h-4 text-violet-800" />}
              title={`Why is the LLM Impact Score ${impactScore}?`}
              content="This is based on the article's prominence (headline, feature, lede, or passing mention classification), readership, social engagement, domain authority, and sentiment."
            >
              <GaugeIcon className="w-6 h-6" />
              <p className="font-semibold text-sm text-center">{impactScore}</p>
            </SentimentTooltip>
          </div>
        </div>

        <Separator />
        <div>
          <SectionHeader
            title={isAISentiment ? "AI Sentiment" : "Sentiment"}
            tooltipTitle={
              <div className="flex items-center space-x-2 mb-0.5">
                <h6 className="font-bold whitespace-nowrap leading-4">
                  What is AI Sentiment?
                </h6>
              </div>
            }
            tooltipContent="Handraise's proprietary AI Sentiment technology looks specifically through the lens of your brand, as a human would, to gauge the emotional tone conveyed in a piece of content, whether positive, negative, or neutral. We assess sentiment by analyzing context and language patterns to determine how a reasonable person would interpret the emotions expressed. This allows us to measure public perception and reactions more accurately across media."
            tooltipIcon={<Sparkle />}
            showSparkle={isAISentiment}
            titleAttr={rationale ?? undefined}
          />
          <ArticleSentimentPolarity
            sentimentPolarity={sentimentPolarity}
            sentimentRationale={sentimentRationale}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ScoreDisplay;
